// frontend/src/utils/conversation/buildConversationState.js

import { evaluateFrame } from '../frame/frameEvaluator';
import buildFrameControlState from '../frame/buildFrameControlState';
import { buildReferenceState } from '../reference/referenceTracker';
import { buildTopicState } from '../reference/topicTracker';
import {
  buildPendingQuestionState,
  reconcilePendingQuestionAuthority,
} from '../reference/pendingQuestionTracker';
import { buildPronounResolutionSignal } from './buildPronounResolutionSignal';
import { buildTurnSignalArbitration } from './buildTurnSignalArbitration';
import { buildTurnAttachmentState } from './turnAttachmentTracker';
import { buildAdmissibilityState } from './buildAdmissibilityState';
import buildObjectiveDecisionState from './buildObjectiveDecisionState';
import detectRelationshipStateUpdate from '../relationships/detectRelationshipStateUpdate';
import buildRelationshipCorrectionCandidate from '../interpretation/buildRelationshipCorrectionCandidate';
import buildRelationshipReferenceState from '../relationships/buildRelationshipReferenceState';
import buildActiveFrameState from '../frame/buildActiveFrameState';

import {
  buildEntityState,
  buildLegacyReferenceFromEntityState,
} from '../reference/buildEntityState';

import { debugLog } from '../logging/debugLog';

function normalizeRecentMessages(messages = [], limit = 8) {
  if (!Array.isArray(messages)) return [];

  return messages.slice(-limit).map((msg) => ({
    sender: msg.sender || 'unknown',
    text: msg.text || '',
  }));
}

function getLastAssistantMessage(messages = []) {
  if (!Array.isArray(messages) || messages.length === 0) return '';

  for (let i = messages.length - 1; i >= 0; i -= 1) {
    if (messages[i]?.sender === 'bot') {
      return messages[i]?.text || '';
    }
  }

  return '';
}

function normalizeMessageText(message = '') {
  return String(message || '')
    .trim()
    .toLowerCase()
    .replace(/[.,!?]+$/g, '');
}

function isFreshOpenMessage(message = '') {
  const normalized = normalizeMessageText(message);

  if (!normalized) return false;

  const freshOpeners = new Set([
    'hi',
    'hello',
    'hey',
    'hiya',
    'yo',
    'hello daisy',
    'hi daisy',
    'hey daisy',
    'good morning',
    'good afternoon',
    'good evening',
    'morning',
    'afternoon',
    'evening',
  ]);

  return freshOpeners.has(normalized);
}

const COMPOUND_REFERENCE_PRONOUNS = new Set([
  'he', 'him', 'his', 'she', 'her', 'hers',
  'they', 'them', 'their', 'theirs', 'it', 'its',
]);

const COMPOUND_STATE_CONNECTORS = new Set([
  'am', 'is', 'are', 'was', 'were', 'be', 'been', 'being',
  'seem', 'seems', 'seemed', 'look', 'looks', 'looked',
  'feel', 'feels', 'felt', 'doing', 'did', 'got', 'gets',
  'getting', 'has', 'have', 'had',
]);

const COMPOUND_NON_VALUE_WORDS = new Set([
  'a', 'an', 'and', 'also', 'anyway', 'as', 'at', 'by', 'do',
  'for', 'from', 'i', 'in', 'just', 'of', 'oh', 'on', 'or',
  'so', 'that', 'the', 'then', 'to', 'too', 'you',
]);

function tokenizeCompoundSegment(value = '') {
  return String(value || '')
    .toLowerCase()
    .replace(/[‘’]/g, "'")
    .replace(/\b(he|she|it)'s\b/g, '$1 is')
    .replace(/\b(they|we|you)'re\b/g, '$1 are')
    .replace(/\bi'm\b/g, 'i am')
    .replace(/\b(he|she|it|they|we|you)'ve\b/g, '$1 have')
    .match(/[a-z0-9]+(?:'[a-z0-9]+)?/g) || [];
}

function getPendingTargetTokens(pendingQuestion = null) {
  const target = pendingQuestion?.targetEntity || null;
  const name = String(
    target?.name ||
      [target?.firstName || target?.first_name, target?.lastName || target?.last_name]
        .filter(Boolean)
        .join(' ')
  ).trim();

  return new Set(tokenizeCompoundSegment(name));
}

function isCompoundPendingAnswer({
  precedingSegment = '',
  pendingQuestion = null,
} = {}) {
  const tokens = tokenizeCompoundSegment(precedingSegment);
  if (!tokens.length || !pendingQuestion) return false;

  const targetTokens = getPendingTargetTokens(pendingQuestion);
  const questionPronouns = new Set(
    tokenizeCompoundSegment(pendingQuestion?.text).filter((token) =>
      COMPOUND_REFERENCE_PRONOUNS.has(token)
    )
  );

  const referenceIndexes = tokens
    .map((token, index) => {
      if (targetTokens.has(token)) return index;

      if (COMPOUND_REFERENCE_PRONOUNS.has(token)) {
        if (targetTokens.size || questionPronouns.size) return index;
      }

      return -1;
    })
    .filter((index) => index >= 0);

  return referenceIndexes.some((referenceIndex) => {
    const stateIndex = tokens.findIndex(
      (token, index) =>
        index > referenceIndex && COMPOUND_STATE_CONNECTORS.has(token)
    );

    if (stateIndex < 0) return false;

    return tokens.slice(stateIndex + 1).some(
      (token) =>
        !COMPOUND_NON_VALUE_WORDS.has(token) &&
        !COMPOUND_REFERENCE_PRONOUNS.has(token) &&
        !COMPOUND_STATE_CONNECTORS.has(token)
    );
  });
}

function promoteCompoundPendingAnswer({
  pendingQuestionState = null,
  pendingQuestion = null,
  relationshipReferenceState = null,
} = {}) {
  if (!pendingQuestionState || !pendingQuestion) return pendingQuestionState;
  if (pendingQuestionState.detectedAnswerToPendingQuestion) return pendingQuestionState;

  const requestBoundary = relationshipReferenceState?.requestBoundary || null;
  const precedingSegment = String(requestBoundary?.precedingSegment || '').trim();

  if (
    !relationshipReferenceState?.requestedResolution ||
    !requestBoundary?.memoryVerb ||
    !precedingSegment ||
    !isCompoundPendingAnswer({ precedingSegment, pendingQuestion })
  ) {
    return pendingQuestionState;
  }

  return {
    ...pendingQuestionState,
    detectedAnswerToPendingQuestion: true,
    answerSignal: 'substantive',
    shouldAutoReplyLocally: false,
    nextPendingQuestion: null,
    pendingAuthorityRevoked: false,
    closePendingQuestion: true,
    compoundAnswerSegment: precedingSegment,
    answerSource: 'compound_turn_pre_request_segment',
  };
}

function buildConversationState({
  message = '',
  memory = {},
  relationships = [],
  relationshipNetwork = null,
  messages = [],
  activeIdentity = null,
  pendingQuestion = null,
  pendingSelectionSignals = null,
  explicitRelationshipDeclaration = null,
} = {}) {
  const rawCurrentFrame = memory?.frame || null;
  const rawCurrentEntity = memory?.lastReferencedEntity || null;
  const rawCurrentTopic = memory?.lastReferencedTopic || null;
  const rawCurrentThread = memory?.lastReferencedThread || null;
  const rawCurrentPendingQuestion =
    pendingQuestion || memory?.pendingQuestion || null;
  const rawCurrentObjective = memory?.activeObjective || null;
  const rawCurrentDecision = memory?.activeDecision || null;
  const rawCurrentReasoningChain = memory?.activeReasoningChain || [];

  const isFreshOpen = isFreshOpenMessage(message);
  const shouldResetEphemeral = isFreshOpen;

  const currentFrame = shouldResetEphemeral ? null : rawCurrentFrame;
  const currentEntity = shouldResetEphemeral ? null : rawCurrentEntity;
  const currentTopic = shouldResetEphemeral ? null : rawCurrentTopic;
  const currentThread = shouldResetEphemeral ? null : rawCurrentThread;
  const currentPendingQuestion = shouldResetEphemeral
    ? null
    : rawCurrentPendingQuestion;
  const currentObjective = shouldResetEphemeral ? null : rawCurrentObjective;
  const currentDecision = shouldResetEphemeral ? null : rawCurrentDecision;
  const currentReasoningChain = shouldResetEphemeral ? [] : rawCurrentReasoningChain;

  const recentMessages = normalizeRecentMessages(messages, 8);
  const lastAssistantMessage = getLastAssistantMessage(messages);

  const frame = evaluateFrame({
    message,
    currentFrame,
  });

  const control = buildFrameControlState({
    message,
    currentFrame,
  });

  const referenceState = buildReferenceState({
    message,
    relationships,
    currentEntity,
  });

  const relationshipStateCandidate = detectRelationshipStateUpdate({
    message,
    relationships,
    lastReferencedEntity: currentEntity,
  });

  if (relationshipStateCandidate) {
    debugLog(
      '🧠 [ConversationState] Relationship state candidate:',
      relationshipStateCandidate
    );
  }

  const relationshipCorrectionCandidate = buildRelationshipCorrectionCandidate({
    message,
    relationships,
    lastReferencedEntity: currentEntity,
  });

  if (relationshipCorrectionCandidate) {
    debugLog(
      '🧠 [ConversationState] Relationship correction candidate:',
      relationshipCorrectionCandidate
    );
  }

  const topicState = buildTopicState({
    message,
    currentTopic,
    currentThread,
  });

  const entityState = buildEntityState({
    message,
    relationships,
    currentEntity,
    previousActiveEntity: currentEntity,
    candidateEntity: referenceState.lastReferencedEntity,
    pronounCandidateEntity: referenceState.lastReferencedEntity,
    isFreshOpen,
  });

  const entityStateActive =
    buildLegacyReferenceFromEntityState(entityState) || null;

  const relationshipReferenceState = buildRelationshipReferenceState({
    message,
    lastAssistantMessage,
    currentEntity: entityStateActive || currentEntity || null,
    entityState,
    relationships,
    relationshipNetwork,
  });

  const relationallyResolvedEntity =
    relationshipReferenceState?.resolved === true
      ? relationshipReferenceState.primaryEntity
      : null;

  if (relationallyResolvedEntity) {
    debugLog('🕸️ [RelationshipReferenceState]', {
      target: relationallyResolvedEntity.name || null,
      role: relationshipReferenceState.targetRole || null,
      confidence: relationshipReferenceState.confidence || 0,
      reason: relationshipReferenceState.reason || null,
    });
  }

  const turnAttachment = buildTurnAttachmentState({
    message,
    lastAssistantMessage,
    pendingQuestion: currentPendingQuestion,
    currentTopic,
    currentFrame,
    candidateEntity:
      relationallyResolvedEntity ||
      entityStateActive ||
      referenceState.lastReferencedEntity,
    candidateTopic: topicState.lastReferencedTopic,
    entityState,
  });

  const resolvedFromEntityState =
    entityState?.active?.signals?.resolved === true
      ? entityState.active
      : null;

  const resolvedFromAttachment =
    turnAttachment?.resolvedEntity?.signals?.resolved === true
      ? turnAttachment.resolvedEntity
      : null;

  const finalReferencedEntity =
    relationallyResolvedEntity ||
    resolvedFromEntityState ||
    resolvedFromAttachment ||
    turnAttachment.resolvedEntity ||
    entityStateActive ||
    referenceState.lastReferencedEntity ||
    currentEntity ||
    null;

  const finalReferencedTopic =
    turnAttachment.resolvedTopic ||
    topicState.lastReferencedTopic ||
    currentTopic ||
    null;

  const finalReferencedThread =
    topicState.lastReferencedThread ||
    currentThread ||
    null;

  const objectiveDecisionState = buildObjectiveDecisionState({
    message,
    currentObjective,
    currentDecision,
    currentReasoningChain,
    lastReferencedTopic: finalReferencedTopic,
    lastReferencedThread: finalReferencedThread,
    isFreshOpen,
  });

  const previousPendingTargetEntity =
    currentPendingQuestion?.targetEntity || currentEntity || null;

  const previousPendingTargetTopic =
    currentPendingQuestion?.targetTopic || currentTopic || null;

  const currentNamedEntities = Array.isArray(entityState?.named)
    ? entityState.named
    : [];

  const previousNamedEntities = Array.isArray(currentPendingQuestion?.targetNamed)
    ? currentPendingQuestion.targetNamed
    : previousPendingTargetEntity
      ? [previousPendingTargetEntity]
      : [];

  const basePendingQuestionState = buildPendingQuestionState({
    inboundMessage: message,
    pendingQuestion: currentPendingQuestion,
    lastAssistantMessage,
    frame,
    lastReferencedEntity: finalReferencedEntity,
    lastReferencedTopic: finalReferencedTopic,
    currentEntity: finalReferencedEntity,
    previousEntity: previousPendingTargetEntity,
    currentTopic: finalReferencedTopic,
    previousTopic: previousPendingTargetTopic,
    currentNamed: currentNamedEntities,
    previousNamed: previousNamedEntities,
  });

  const pendingQuestionState = promoteCompoundPendingAnswer({
    pendingQuestionState: basePendingQuestionState,
    pendingQuestion: currentPendingQuestion,
    relationshipReferenceState,
  });

  if (pendingQuestionState?.answerSource === 'compound_turn_pre_request_segment') {
    debugLog('🧩 [CompoundPendingAnswer]', {
      pendingQuestion: currentPendingQuestion?.text || null,
      answerSegment: pendingQuestionState.compoundAnswerSegment || null,
      answerSignal: pendingQuestionState.answerSignal || null,
      closePendingQuestion: pendingQuestionState.closePendingQuestion === true,
      activeRequestRole: relationshipReferenceState?.requestedRole || null,
      activeRequestTarget:
        relationshipReferenceState?.primaryEntity?.name || null,
    });
  }

  const admissibilityState = buildAdmissibilityState({
    message,
    pendingQuestionState,
    entityState,
    frame,
    explicitRelationshipDeclaration,
  });

  const pronounResolutionSignal = buildPronounResolutionSignal({
    message,
    activeIdentity,
    entityState,
  });

  const turnSignalArbitration = buildTurnSignalArbitration({
    message,
    pendingQuestionState,
    pendingSelectionSignals,
    referenceState: {
      lastReferencedEntity: finalReferencedEntity,
      lastReferencedTopic: finalReferencedTopic,
      lastReferencedThread: finalReferencedThread,
      topicContinuity: topicState?.continuity || null,
    },
    turnAttachment,
    currentEntity: finalReferencedEntity || currentEntity || null,
    pronounResolutionSignal,
    admissibilityState,
    objectiveDecisionState,
  });

  const governedPendingQuestionState = reconcilePendingQuestionAuthority({
    pendingQuestionState,
    turnSignalArbitration,
  });

  const resolvedPendingQuestion = governedPendingQuestionState?.closePendingQuestion
    ? null
    : governedPendingQuestionState?.nextPendingQuestion || currentPendingQuestion || null;

  const reference = {
    lastReferencedEntity: finalReferencedEntity,
    lastReferencedTopic: finalReferencedTopic,
    lastReferencedThread: finalReferencedThread,
  };

  const lifecycle = {
    isFreshOpen,
    shouldResetEphemeral,
  };

  const activeFrameState = buildActiveFrameState({
    message,
    frame,
    control,
    objectiveDecisionState,
    entityState,
    relationshipReferenceState,
    reference,
    topicState,
    turnAttachment,
    pendingQuestionState: governedPendingQuestionState,
    pendingQuestion: currentPendingQuestion,
    pronounResolutionSignal,
    lifecycle,
  });

  debugLog('🧭 [ActiveFrameState]', activeFrameState);

  return {
    identity: {
      activeIdentity,
      relationships,
      userName: memory?.userName || activeIdentity?.displayName || 'Friend',
    },
    frame,
    control,
    entityState,
    topicState,
    admissibilityState,
    objectiveDecisionState,
    relationshipStateCandidate,
    relationshipCorrectionCandidate,
    relationshipReferenceState,
    activeFrameState,
    turnAttachment,
    reference,
    pendingQuestion: resolvedPendingQuestion,
    pendingQuestionState: governedPendingQuestionState,
    pronounResolutionSignal,
    turnSignalArbitration,
    recentMessages,
    inboundMessage: message,
    lifecycle,
  };
}

export default buildConversationState;